<template>
  <div class="visualisasi-page">
    <header class="page-header">
      <div>
        <router-link to="/visualisasi" class="back-link">&larr; Visualisasi lokal</router-link>
        <p class="eyebrow">FUSEKI LIVE DATA</p>
        <h1>Graph Ontologi Wayang Purwa</h1>
        <p class="page-intro">Struktur kelas dan relasi objek yang dibaca langsung dari basis pengetahuan Fuseki.</p>
      </div>
      <div class="graph-meta" aria-label="Ringkasan graph">
        <span class="meta-value">{{ graphNodes.length }}</span><span class="meta-label">kelas</span>
        <span class="meta-divider" aria-hidden="true"></span>
        <span class="meta-value">{{ graphEdges.length }}</span><span class="meta-label">relasi</span>
      </div>
    </header>

    <section class="graph-panel">
      <div class="graph-toolbar">
        <span class="toolbar-label"><span class="status-dot" :class="{ loading: isLoading }"></span>{{ statusText }}</span>
        <span class="toolbar-hint">Klik node untuk melihat detail</span>
      </div>
      <div v-if="isLoading" class="graph-state">Mengambil struktur ontologi dari Fuseki...</div>
      <div v-else-if="error" class="graph-state error-state">{{ error }}</div>
      <div v-else-if="!graphNodes.length" class="graph-state">Data graph belum tersedia dari Fuseki.</div>
      <div v-else ref="graphContainer" class="network" aria-label="Graph ontologi dari Fuseki"></div>
    </section>

    <section v-if="selectedNode" class="detail">
      <div class="detail-heading"><span class="detail-kicker">KELAS TERPILIH</span><h2>{{ selectedNode.label }}</h2></div>
      <div class="detail-fields">
        <div class="detail-field"><span>IRI</span><strong>{{ selectedNode.iri }}</strong></div>
        <div class="detail-field"><span>Jenis</span><strong>{{ selectedNode.kind }}</strong></div>
      </div>
      <p v-if="selectedNode.comment" class="description">{{ selectedNode.comment }}</p>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import cytoscape from 'cytoscape'

const endpoint = 'https://app.alunalun.info/fuseki/wayangpurwa/sparql'
// const ontologyPrefix = 'http://ukdw.ac.id/ontologi/wayangpurwa#'

export default {
  name: 'VisualisasiFuseki',
  data() {
    return { graphNodes: [], graphEdges: [], selectedNode: null, network: null, isLoading: true, error: null }
  },
  computed: {
    statusText() {
      if (this.isLoading) return 'Menghubungkan ke Fuseki'
      if (this.error) return 'Koneksi gagal'
      return 'Fuseki terhubung'
    }
  },
  async mounted() { await this.loadGraph() },
  beforeUnmount() { this.destroyGraph() },
  methods: {
    async loadGraph() {
      this.isLoading = true
      this.error = null
      const query = `
        PREFIX owl: <http://www.w3.org/2002/07/owl#>
        PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
        PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
        PREFIX wp: <http://ukdw.ac.id/ontologi/wayangpurwa#>
        PREFIX ontodebug: <http://ainf.aau.at/ontodebug#>
        PREFIX wayangpurwa: <http://ontology/wayangpurwa#>
        PREFIX ontology: <http://a.com/ontology#>

        SELECT DISTINCT
            ?source
            ?predicate
            ?target
            ?sourceLabel
            ?targetLabel
            ?comment
        WHERE {

        # =========================================================
        # 1. Hubungan antar-class: subclass
        # =========================================================
        {
            ?source rdfs:subClassOf ?target .

            BIND(rdfs:subClassOf AS ?predicate)

            FILTER(?source != owl:Thing)
            FILTER(isIRI(?source))
            FILTER(isIRI(?target))

            OPTIONAL {
            ?source rdfs:label ?sourceLabel
            }

            OPTIONAL {
            ?target rdfs:label ?targetLabel
            }

            OPTIONAL {
            ?source rdfs:comment ?comment
            }
        }

        UNION

        # =========================================================
        # 2. Hubungan class berdasarkan domain-range ObjectProperty
        # =========================================================
        {
            ?property a owl:ObjectProperty ;
                    rdfs:domain ?source ;
                    rdfs:range ?target .

            BIND(?property AS ?predicate)

            FILTER(isIRI(?source))
            FILTER(isIRI(?target))

            OPTIONAL {
            ?source rdfs:label ?sourceLabel
            }

            OPTIONAL {
            ?target rdfs:label ?targetLabel
            }

            OPTIONAL {
            ?source rdfs:comment ?comment
            }
        }

        UNION

        # =========================================================
        # 3. Hubungan aktual antar-individual/object
        # =========================================================
        {
            ?source ?predicate ?target .

            FILTER(isIRI(?source))
            FILTER(isIRI(?target))

            # Hindari relasi schema yang sudah ditangani di atas
            FILTER(?predicate != rdf:type)
            FILTER(?predicate != rdfs:subClassOf)
            FILTER(?predicate != rdfs:label)
            FILTER(?predicate != rdfs:comment)

            OPTIONAL {
            ?source rdfs:label ?sourceLabel
            }

            OPTIONAL {
            ?target rdfs:label ?targetLabel
            }

            OPTIONAL {
            ?source rdfs:comment ?comment
            }
        }

        # Hanya resource ontology Wayang Purwa
        FILTER(
            STRSTARTS(
            STR(?source),
            "http://ukdw.ac.id/ontologi/wayangpurwa#"
            )
        )

        FILTER(
            STRSTARTS(
            STR(?target),
            "http://ukdw.ac.id/ontologi/wayangpurwa#"
            )
        )
        }
        ORDER BY ?source ?predicate ?target
        LIMIT 300
      `

      try {
        const response = await axios.get(endpoint, {
          params: { query, format: 'json' },
          headers: { Accept: 'application/sparql-results+json' },
          timeout: 15000
        })
        const bindings = response.data?.results?.bindings || []

        console.log(`[VisualisasiFuseki] ${bindings.length} binding diterima dari Fuseki`)

        const nodeMap = new Map()
        const edgeMap = new Map()

        bindings.forEach((binding, index) => {
          const source = this.bindingValue(binding.source)
          const predicate = this.bindingValue(binding.predicate)
          const target = this.bindingValue(binding.target)

          // Lewati hanya binding yang benar-benar tidak lengkap.
          if (!source || !predicate || !target) {
            console.warn('[VisualisasiFuseki] Binding dilewati karena tidak lengkap:', binding)
            return
          }

          const sourceLabel = this.bindingValue(binding.sourceLabel)
          const targetLabel = this.bindingValue(binding.targetLabel)
          const comment = this.bindingValue(binding.comment)

          if (!nodeMap.has(source)) {
            nodeMap.set(
              source,
              this.makeNode(
                source,
                sourceLabel,
                comment,
                this.nodeKind(source, predicate)
              )
            )
          } else if (comment && !nodeMap.get(source).data.comment) {
            nodeMap.get(source).data.comment = comment
          }

          if (!nodeMap.has(target)) {
            nodeMap.set(
              target,
              this.makeNode(
                target,
                targetLabel,
                '',
                this.nodeKind(target, predicate)
              )
            )
          }

          // index dipakai agar relasi paralel tetap memiliki id unik.
          const edgeKey = `${source}|${predicate}|${target}|${index}`

          edgeMap.set(edgeKey, {
            data: {
              id: edgeKey,
              source,
              target,
              iri: predicate,
              label: this.formatName(predicate)
            }
          })
        })

        this.graphNodes = [...nodeMap.values()]
        this.graphEdges = [...edgeMap.values()]

        console.log(
          `[VisualisasiFuseki] Graph siap: ${this.graphNodes.length} node, ${this.graphEdges.length} edge`
        )

        this.isLoading = false
        await this.$nextTick()
        this.drawGraph()
      } catch (err) {
        const serverMessage = err.response?.data?.message || err.response?.data?.error
        this.error = serverMessage
          ? `Fuseki menolak query: ${serverMessage}`
          : 'Graph Fuseki tidak dapat dimuat. Periksa koneksi ke endpoint SPARQL.'
        this.isLoading = false
        console.error(err)
      }
    },
    drawGraph() {
      if (!this.$refs.graphContainer || !this.graphNodes.length) return
      this.destroyGraph()
      this.network = cytoscape({
        container: this.$refs.graphContainer,
        elements: { nodes: this.graphNodes, edges: this.graphEdges },
        style: [
          { selector: 'node', style: { label: 'data(label)', 'text-valign': 'center', 'text-halign': 'center', color: '#fffaf0', 'background-color': '#9a5b22', 'border-color': '#673b1a', 'border-width': 2, 'font-family': 'Plus Jakarta Sans', 'font-size': 13, 'font-weight': 700, 'text-wrap': 'wrap', 'text-max-width': 110, width: 116, height: 56, shape: 'roundrectangle', padding: 8 } },
          { selector: 'node:selected', style: { 'background-color': '#d97706', 'border-color': '#4a260f', 'border-width': 4 } },
          { selector: 'edge', style: { label: 'data(label)', width: 2, 'line-color': '#c58a4b', 'target-arrow-color': '#9a5b22', 'target-arrow-shape': 'triangle', 'curve-style': 'bezier', color: '#6b421e', 'font-family': 'Plus Jakarta Sans', 'font-size': 10, 'font-weight': 700, 'text-background-color': '#fffaf0', 'text-background-opacity': 1, 'text-background-padding': 3, 'text-rotation': 'autorotate' } }
        ],
        layout: { name: 'cose', animate: true, fit: true, padding: 55 }
      })
      this.network.on('tap', 'node', event => { this.selectedNode = event.target.data() })
      this.network.on('tap', event => { if (event.target === this.network) this.selectedNode = null })
    },
    destroyGraph() {
      if (this.network) { this.network.destroy(); this.network = null }
    },
    bindingValue(binding) {
      // Format standar SPARQL Results JSON dari Fuseki:
      // { type: 'uri'|'literal', value: '...' }
      //
      // Fallback kedua dipertahankan agar tetap toleran jika suatu wrapper
      // mengembalikan bentuk { value: { value: '...' } }.
      if (!binding) return ''

      if (typeof binding.value === 'string') {
        return binding.value
      }

      if (
        binding.value &&
        typeof binding.value === 'object' &&
        typeof binding.value.value === 'string'
      ) {
        return binding.value.value
      }

      if (typeof binding === 'string') {
        return binding
      }

      return ''
    },

    nodeKind(iri, predicate) {
      const rdfSchemaPrefix = 'http://www.w3.org/2000/01/rdf-schema#'
      const owlPrefix = 'http://www.w3.org/2002/07/owl#'

      if (
        predicate === `${rdfSchemaPrefix}subClassOf` ||
        predicate.startsWith(rdfSchemaPrefix) ||
        predicate.startsWith(owlPrefix)
      ) {
        return 'Kelas'
      }

      return 'Individu / Resource'
    },

    makeNode(iri, label, comment, kind) {
      return {
        data: {
          id: iri,
          iri,
          label: label || this.formatName(iri),
          comment: comment || '',
          kind
        }
      }
    },
    formatName(value) {
      const localName = (value || '').split('#').pop().split('/').pop()
      return localName.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, character => character.toUpperCase())
    }
  }
}
</script>

<style scoped>
.visualisasi-page { min-height: 100vh; padding: clamp(28px, 5vw, 64px) clamp(20px, 5vw, 72px); background: radial-gradient(circle at 92% 5%, rgba(239, 184, 92, 0.24), transparent 28%), #fff9ed; }
.page-header { max-width: 1240px; margin: 0 auto 24px; display: flex; align-items: end; justify-content: space-between; gap: 24px; }
.back-link { display: inline-block; color: #9a5b22; font-size: 13px; font-weight: 700; margin-bottom: 22px; text-decoration: none; }
.back-link:hover { text-decoration: underline; }
.eyebrow, .detail-kicker { color: #9a5b22; font-size: 11px; font-weight: 800; letter-spacing: .14em; margin: 0 0 8px; }
h1, h2 { color: #3c2414; margin: 0; }
h1 { font-size: clamp(24px, 3vw, 38px); line-height: 1.15; }
.page-intro { color: #76563a; margin: 10px 0 0; }
.graph-meta { display: flex; align-items: baseline; gap: 7px; white-space: nowrap; color: #76563a; }
.meta-value { color: #9a5b22; font-size: 22px; font-weight: 800; }
.meta-label { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; }
.meta-divider { width: 1px; height: 24px; background: #e5c79e; margin: 0 10px; }
.graph-panel, .detail { max-width: 1240px; margin: 0 auto; background: rgba(255, 253, 247, .9); border: 1px solid #ead3ad; border-radius: 14px; box-shadow: 0 18px 40px rgba(95, 61, 20, .1); overflow: hidden; }
.graph-toolbar { min-height: 52px; padding: 0 20px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #efdfc4; color: #76563a; font-size: 12px; }
.toolbar-label { display: inline-flex; align-items: center; gap: 8px; color: #3c2414; font-weight: 800; }
.status-dot { width: 8px; height: 8px; border-radius: 50%; background: #3d9b62; box-shadow: 0 0 0 4px #dff0e4; }
.status-dot.loading { background: #d97706; box-shadow: 0 0 0 4px #f9e5c5; }
.network { height: min(68vh, 680px); min-height: 480px; background-color: #fffdf7; background-image: radial-gradient(#ead8ba 1px, transparent 1px); background-size: 22px 22px; }
.graph-state { min-height: 480px; display: grid; place-items: center; padding: 24px; color: #76563a; text-align: center; }
.error-state { color: #9f3d2d; }
.detail { margin-top: 24px; padding: 22px 24px; }
.detail-heading h2 { font-size: 22px; }
.detail-fields { display: flex; flex-wrap: wrap; gap: 12px 48px; margin-top: 18px; }
.detail-field { display: grid; gap: 3px; min-width: 180px; }
.detail-field span { color: #9a6c42; font-size: 11px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.detail-field strong { color: #573618; font-size: 14px; overflow-wrap: anywhere; }
.description { color: #76563a; margin: 18px 0 0; max-width: 760px; }
@media (max-width: 640px) { .page-header { align-items: start; flex-direction: column; } .graph-toolbar { align-items: start; flex-direction: column; gap: 4px; justify-content: center; padding: 12px 16px; } .network, .graph-state { min-height: 410px; height: 62vh; } .detail { padding: 20px; } }
</style>